import { Injectable, inject, signal, computed, untracked } from '@angular/core';
import { toSignal, toObservable }               from '@angular/core/rxjs-interop';
import { switchMap, filter, catchError, map }    from 'rxjs/operators';
import { of }                                    from 'rxjs';
import { Router }                                from '@angular/router';
import { TranslateService }                      from '@ngx-translate/core';

import { HttpService }  from 'src/app/services/http.service';
import { TreeNode }     from '@libs/front/components/tree-view/tree-view.component';
import { BlastFlatDto } from 'src/app/shared/models/site-tree.dto';
import { Site }         from 'src/app/bo/site';
import {
  TreeGroupingConfig,
  GroupingLevel,
  DEFAULT_TREE_GROUPING,
} from 'src/app/shared/models/tree-grouping.types';

// ── Types publics ─────────────────────────────────────────────────────────────

export type SiteNodeType =
  | 'project'
  | 'zone'
  | 'year'
  | 'month'
  | 'niveau'
  | 'type'
  | 'blast';

export type SiteTreeStatusFilter = 'all' | 'active' | 'completed';

export interface SiteTreeFilters {
  activeProjectsOnly: boolean;
  activeBlastsOnly: boolean;
  projects: string[];
  zones: string[];
  types: string[];
  status: SiteTreeStatusFilter;
  level: string | null;
  startDate: string | null;
  endDate: string | null;
}

// ── Clés localStorage ─────────────────────────────────────────────────────────

const LS_SITE_ID       = 'siteNav.siteId';
const LS_PROJECT_ID    = 'siteNav.projectId';
const LS_SELECTED_NODE = 'siteNav.selectedNodeId';
const LS_GROUPING      = 'siteNav.grouping';
const LS_FILTERS       = 'siteNav.filters';
const LS_UNCHECKED_BLASTS = 'siteNav.uncheckedBlastIds';

// ── Helpers localStorage ──────────────────────────────────────────────────────

function lsLoad(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function lsSave(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch {}
}

function lsClear(key: string): void {
  try { localStorage.removeItem(key); } catch {}
}

function migrateGroupingLevel(level: unknown): GroupingLevel {
  return level === 'status' ? 'type' : (level as GroupingLevel);
}

/** Regroupement choisi dans le panneau de l'arbre pour ce site, ou null s'il suit la configuration du site. */
function lsLoadGrouping(siteId: number): TreeGroupingConfig | null {
  try {
    const raw = localStorage.getItem(`${LS_GROUPING}.${siteId}`);
    if (raw) {
      const merged = { ...DEFAULT_TREE_GROUPING, ...JSON.parse(raw) } as TreeGroupingConfig;
      return {
        level1: migrateGroupingLevel(merged.level1) as Exclude<GroupingLevel, 'none'>,
        level2: migrateGroupingLevel(merged.level2),
        level3: migrateGroupingLevel(merged.level3),
      };
    }
  } catch {}
  return null;
}

/** Niveaux de `ConfigurationTreeviewEnum`, dans l'ordre de l'enum, tels que l'arbre les nomme. */
const SITE_CONFIG_LEVELS: GroupingLevel[] = ['project', 'zone', 'type', 'niveau', 'year', 'month', 'none'];

/**
 * Hiérarchie des tirs configurée sur le site (`Configuration > Sites`), sérialisée par
 * l'ancien front en `{ niveauUn, niveauDeux, niveauTrois }` d'après `ConfigurationTreeviewEnum`.
 */
function siteGroupingFromConfig(raw: string | null | undefined): TreeGroupingConfig {
  if (!raw) return { ...DEFAULT_TREE_GROUPING };
  try {
    const config = JSON.parse(raw) as { niveauUn?: number; niveauDeux?: number; niveauTrois?: number };
    const toLevel = (value: number | undefined): GroupingLevel => SITE_CONFIG_LEVELS[value ?? -1] ?? 'none';

    const level1 = toLevel(config.niveauUn);
    if (level1 === 'none') return { ...DEFAULT_TREE_GROUPING };

    const rawLevel2 = toLevel(config.niveauDeux);
    const level2    = rawLevel2 === level1 ? 'none' : rawLevel2;
    const level3    = level2 === 'none' ? 'none' : toLevel(config.niveauTrois);
    return {
      level1,
      level2,
      level3: level3 === level1 || level3 === level2 ? 'none' : level3,
    };
  } catch {
    return { ...DEFAULT_TREE_GROUPING };
  }
}

function defaultFilters(): SiteTreeFilters {
  return {
    activeProjectsOnly: false,
    activeBlastsOnly: false,
    projects: [],
    zones: [],
    types: [],
    status: 'all',
    level: null,
    startDate: null,
    endDate: null,
  };
}

function lsLoadFilters(): SiteTreeFilters {
  try {
    const raw = localStorage.getItem(LS_FILTERS);
    if (raw) return { ...defaultFilters(), ...JSON.parse(raw) };
  } catch {}
  return defaultFilters();
}

function lsLoadUncheckedBlasts(siteId: number): Set<number> {
  try {
    const raw = localStorage.getItem(`${LS_UNCHECKED_BLASTS}.${siteId}`);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed)) return new Set(parsed.map(Number));
  } catch {}
  return new Set();
}

// ── Helpers date / grouping ───────────────────────────────────────────────────

const NOT_SET = 'Not set';

function getBestDate(blast: BlastFlatDto): Date | null {
  const d = blast.dateTirEffective ?? blast.dateTirPrevisionnelle;
  return d ? new Date(d) : null;
}

function getGroupValue(blast: BlastFlatDto, level: GroupingLevel): string {
  switch (level) {
    case 'project': return blast.nomProjet  || NOT_SET;
    case 'zone':    return blast.nomZone    || NOT_SET;
    case 'niveau':  return blast.niveau     || NOT_SET;
    // Regroupement par type de tir (affiché "Status" — cf. ancien front).
    case 'type':    return getBlastType(blast) || NOT_SET;
    case 'year': {
      const d = getBestDate(blast);
      return d ? String(d.getFullYear()) : NOT_SET;
    }
    case 'month': {
      // Clé "YYYY-MM", indépendante de la langue : le nom du mois n'est que dans le libellé (monthLabel).
      const d = getBestDate(blast);
      if (!d) return NOT_SET;
      const mm = ('0' + (d.getMonth() + 1)).slice(-2);
      return `${d.getFullYear()}-${mm}`;
    }
    default: return '';
  }
}

const MONTH_KEY = /^(\d{4}-\d{2})(?: - .*)?$/;

/** Libellé d'un groupe mois "YYYY-MM" : "2026-10 - October" en anglais, "2026-10 - Octobre" en français. */
export function monthGroupLabel(key: string, lang: string | undefined): string {
  if (!/^\d{4}-\d{2}$/.test(key)) return key;
  const [year, month] = key.split('-').map(Number);
  const name = new Date(year, month - 1, 1).toLocaleDateString(lang || undefined, { month: 'long' });
  return `${key} - ${name.charAt(0).toUpperCase()}${name.slice(1)}`;
}

/**
 * Valeur d'un nœud lue dans l'URL. Un mois s'y trouvait avec son nom en français
 * ("2026-10 - Octobre") ; sa clé est désormais "2026-10", à laquelle les anciens liens sont ramenés.
 */
export function nodeKeyFromUrl(nodeType: string | null, nodeValue: string | null): string | null {
  if (nodeType !== 'month' || !nodeValue) return nodeValue;
  return MONTH_KEY.exec(nodeValue)?.[1] ?? nodeValue;
}

function getBlastType(blast: BlastFlatDto): string {
  return ((blast as { nomTypeTir?: string | null }).nomTypeTir ?? '').trim();
}

function projectEndTime(blasts: BlastFlatDto[]): number {
  const d = blasts.length ? blasts[blasts.length - 1].dateFinProjet : null;
  return d ? new Date(d).getTime() : 0;
}

function sortGroupKeys(groups: Map<string, BlastFlatDto[]>, level: GroupingLevel): string[] {
  const keys = [...groups.keys()];
  switch (level) {
    case 'project':
      // Date de fin de projet décroissante (projets sans date => epoch 0 => en dernier).
      return keys.sort((a, b) => projectEndTime(groups.get(b)!) - projectEndTime(groups.get(a)!));

    case 'year':
      // Croissant avec "Not set" (sans date) en dernier, puis inversion => décroissant, "Not set" en premier.
      keys.sort((a, b) => {
        const av = a === NOT_SET ? null : Number(a);
        const bv = b === NOT_SET ? null : Number(b);
        if (av === bv) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return av - bv;
      });
      return keys.reverse();

    case 'month':
      // Clés "YYYY-MM" : année puis mois décroissants.
      return keys.sort((a, b) => {
        const [ay, am] = a.split('-').map(Number);
        const [by, bm] = b.split('-').map(Number);
        return ay !== by ? by - ay : bm - am;
      });

    case 'niveau':
      // Alphabétique croissant, non-renseigné ("Not set") en dernier.
      return keys.sort((a, b) => {
        if (a === b) return 0;
        if (a === NOT_SET) return 1;
        if (b === NOT_SET) return -1;
        return a.localeCompare(b);
      });

    default: // zone, type
      return keys.sort((a, b) => a.localeCompare(b));
  }
}

function sortLeafBlasts(blasts: BlastFlatDto[], lastLevel: GroupingLevel): BlastFlatDto[] {
  switch (lastLevel) {
    case 'year':
    case 'month':
      return blasts.sort((a, b) => b.nom.localeCompare(a.nom));   // nom décroissant
    case 'niveau':
      return blasts.sort((a, b) => a.nom.localeCompare(b.nom));   // nom croissant
    default: // project, zone, type
      // Meilleure date décroissante (sans date => en dernier), puis nom croissant.
      return blasts.sort((a, b) => {
        const ta = getBestDate(a)?.getTime() ?? 0;
        const tb = getBestDate(b)?.getTime() ?? 0;
        return ta !== tb ? tb - ta : a.nom.localeCompare(b.nom);
      });
  }
}

// ── Service ───────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class SiteNavigationService {

  private readonly http   = inject(HttpService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  /** Langue d'Explore : les mois de l'arbre s'affichent dans celle-ci, pas dans celle du navigateur. */
  private readonly _lang = toSignal(
    this.translate.onLangChange.pipe(map(event => event.lang)),
    { initialValue: this.translate.currentLang },
  );

  // ── Site courant ──────────────────────────────────────────────────────────
  // Initialisé à null — setSite() déclenche toujours le chargement HTTP.
  // Le localStorage ne sert qu'à mémoriser l'id, pas à déclencher le pipe.

  private readonly _siteId = signal<number | null>(null);

  readonly siteId = this._siteId.asReadonly();

  // ── Project courant ───────────────────────────────────────────────────────

  private readonly _projectId = signal<number | null>(
    Number(lsLoad(LS_PROJECT_ID)) || null
  );

  readonly projectId = this._projectId.asReadonly();

  // ── Nœud sélectionné ─────────────────────────────────────────────────────

  private readonly _selectedNodeId = signal<string>(
    lsLoad(LS_SELECTED_NODE) ?? ''
  );

  readonly selectedNodeId = this._selectedNodeId.asReadonly();

  // ── Config de grouping ────────────────────────────────────────────────────

  // Le site impose sa hiérarchie ; le panneau de l'arbre peut la surcharger, par site.

  private readonly _localGrouping = signal<TreeGroupingConfig | null>(null);

  private readonly _siteGrouping = computed(() => siteGroupingFromConfig(this._site()?.configurationTreeview));

  readonly grouping = computed<TreeGroupingConfig>(() => this._localGrouping() ?? this._siteGrouping());

  private readonly _filters = signal<SiteTreeFilters>(lsLoadFilters());

  readonly filters = this._filters.asReadonly();

  // ── Blasts décochés dans l'arbre (mémorisés par site) ────────────────────

  private readonly _uncheckedBlastIds = signal<ReadonlySet<number>>(new Set());

  // ── Chargement HTTP blasts ────────────────────────────────────────────────
  // GET Sites/{id}/blasts → BlastFlatDto[] (tableau brut)
  // _blastsVersion : incrémenté par reloadBlasts() pour relancer la requête.

  private readonly _blastsVersion = signal(0);

  private readonly _blastsRequest = computed(() => ({
    siteId:  this._siteId(),
    version: this._blastsVersion(),
  }));

  private readonly _data = toSignal(
    toObservable(this._blastsRequest).pipe(
      map(request => request.siteId),
      filter((id): id is number => id !== null),
      switchMap(id =>
        this.http.get<BlastFlatDto[]>(`Sites/${id}/blasts`).pipe(
          catchError(() => of(null))
        )
      )
    ),
    { initialValue: null }
  );

  // ── Chargement HTTP site info ─────────────────────────────────────────────
  // GET Sites/{id} → Site complet (epsg, region, ...)
  // _siteVersion : incrémenté par reloadSite() pour relancer la requête.

  private readonly _siteVersion = signal(0);

  private readonly _siteRequest = computed(() => ({
    siteId:  this._siteId(),
    version: this._siteVersion(),
  }));

  private readonly _site = toSignal(
    toObservable(this._siteRequest).pipe(
      map(request => request.siteId),
      filter((id): id is number => id !== null),
      switchMap(id =>
        this.http.get<Site>(`Sites/${id}`).pipe(
          catchError(() => of(null))
        )
      )
    ),
    { initialValue: null }
  );

  // ── Computed publics ──────────────────────────────────────────────────────

  /** Site complet de l'URL — consommé par LegacySiteContextService. */
  readonly site     = computed(() => this._site());
  readonly siteName = computed(() => this._site()?.nom ?? '');
  /** Nom de chaque blast du site par tirId (indépendant des filtres du treeview). */
  readonly blastNameById = computed<Map<number, string>>(
    () => new Map((this._data() ?? []).map(b => [b.tirId, b.nom]))
  );
  readonly regionId = computed(() => this._site()?.regionId ?? null);
  /** Organisation du site : celle de son area (`Sites/{id}` renvoie `orgId: 0` au niveau du site). */
  readonly orgId    = computed(() => this._site()?.region?.orgId || this._site()?.orgId || null);

  /** Nom du blast parmi les blasts chargés du site (null tant que non chargé ou introuvable). */
  blastName(blastId: number | null | undefined): string | null {
    if (blastId == null) return null;
    return this._data()?.find(b => b.tirId === Number(blastId))?.nom ?? null;
  }

  readonly filterOptions = computed(() => {
    const data = this._data() ?? [];

    const projects = [...new Set(
      data.map(b => b.nomProjet).filter((name): name is string => !!name)
    )].sort((a, b) => a.localeCompare(b));

    const zones = [...new Set(
      data.map(b => b.nomZone).filter((name): name is string => !!name)
    )].sort((a, b) => a.localeCompare(b));

    const types = [...new Set(
      data.map(getBlastType).filter((name): name is string => !!name)
    )].sort((a, b) => a.localeCompare(b));

    const levels = [...new Set(
      data.map(b => b.niveau).filter((name): name is string => !!name)
    )].sort((a, b) => a.localeCompare(b));

    return { projects, zones, types, levels };
  });

  /**
   * Les filtres sont persistés pour l'utilisateur, pas pour le site, mais projets / zones /
   * types / niveau sont des *noms* propres à un site : gardés tels quels, ils ne correspondent
   * à rien sur le site suivant et masquent alors la totalité de ses tirs (treeview vide).
   * On les ramène donc aux valeurs que le site courant propose — une sélection vide voulant
   * déjà dire « aucun filtre ». C'est le même cadrage que celui appliqué à l'ouverture du
   * popup de filtrage, qui sinon affiche « rien de sélectionné » pendant que le filtre stocké
   * continue de tout masquer.
   */
  readonly effectiveFilters = computed<SiteTreeFilters>(() => {
    const f       = this._filters();
    const options = this.filterOptions();
    return {
      ...f,
      projects: f.projects.filter(name => options.projects.includes(name)),
      zones:    f.zones.filter(name => options.zones.includes(name)),
      types:    f.types.filter(name => options.types.includes(name)),
      level:    f.level && options.levels.includes(f.level) ? f.level : null,
    };
  });

  readonly filteredBlasts = computed<BlastFlatDto[]>(() => {
    const data = this._data() ?? [];
    const f = this.effectiveFilters();
    if (!data.length) return [];

    const startDate = f.startDate ? new Date(f.startDate) : null;
    const endDate = f.endDate ? new Date(f.endDate) : null;

    if (startDate) startDate.setHours(0, 0, 0, 0);
    if (endDate) endDate.setHours(23, 59, 59, 999);

    const now = new Date();

    return data.filter(blast => {
      const projectOk = f.projects.length === 0 || f.projects.includes(blast.nomProjet);
      const zoneOk = f.zones.length === 0 || f.zones.includes(blast.nomZone);

      const type = getBlastType(blast);
      const typeOk = f.types.length === 0 || (type !== '' && f.types.includes(type));

      const statusOk =
        f.status === 'all'
          ? true
          : f.status === 'active'
            ? !blast.estRealise
            : blast.estRealise;

      const levelOk = !f.level || blast.niveau === f.level;

      const bestDate = getBestDate(blast);
      const dateOk = startDate && endDate
        ? !!bestDate && bestDate >= startDate && bestDate <= endDate
        : true;

      const activeOnlyOk = !f.activeBlastsOnly || !blast.estRealise;

      // Projet actif = sans date de fin ou date de fin non dépassée (cf. ancien treeview).
      const activeProjectOk = !f.activeProjectsOnly || !blast.dateFinProjet || new Date(blast.dateFinProjet) >= now;

      return projectOk && zoneOk && typeOk && statusOk && levelOk && dateOk && activeOnlyOk && activeProjectOk;
    });
  });

  readonly treeData = computed<TreeNode[]>(() => {
    const data     = this.filteredBlasts();
    const grouping = this.grouping();
    const lang     = this._lang();
    if (!data?.length) return [];
    return this._buildTree(data, grouping, lang);
  });

  readonly blasts = computed<BlastFlatDto[]>(() => this._data() ?? []);

  readonly visibleBlastIds = computed<number[]>(() => {
    const unchecked = this._uncheckedBlastIds();
    return this.filteredBlasts().filter(blast => !unchecked.has(blast.tirId)).map(blast => blast.tirId);
  });

  readonly selectedNodeId$ = computed<string>(() => this._selectedNodeId());

  // ── API publique ──────────────────────────────────────────────────────────

  /**
   * Appeler depuis SiteComponent (constructeur ou ngOnInit).
   * Toujours déclenche le chargement HTTP car _siteId part toujours de null.
   */
  setSite(siteId: number): void {
    lsSave(LS_SITE_ID, String(siteId));
    this._projectId.set(null);
    this._selectedNodeId.set('');
    lsClear(LS_PROJECT_ID);
    lsClear(LS_SELECTED_NODE);
    this._uncheckedBlastIds.set(lsLoadUncheckedBlasts(siteId));
    this._localGrouping.set(lsLoadGrouping(siteId));
    // Pas de garde — on set toujours pour déclencher le pipe
    this._siteId.set(siteId);
  }

  syncFromQueryParams(params: {
    projectId?: number | null;
    blastId?:   number | null;
    nodeType?:  string | null;
    nodeValue?: string | null;
  }): void {
    if (params.projectId) {
      this._projectId.set(params.projectId);
      lsSave(LS_PROJECT_ID, String(params.projectId));
    }

    let nodeId = '';
    if (params.blastId) {
      nodeId = `blast:${params.blastId}`;
    } else if (params.projectId && !params.nodeType) {
      nodeId = `project:${params.projectId}`;
    } else if (params.nodeType && params.nodeValue) {
      // Pour les nœuds de groupement sous un projet connu, inclure le projetId
      // dans le nodeId pour correspondre au format "Zone Nord~123" du tree builder
      nodeId = (params.projectId && params.nodeType !== 'project')
        ? `${params.nodeType}:${params.nodeValue}~${params.projectId}`
        : `${params.nodeType}:${params.nodeValue}`;
    }

    this._selectedNodeId.set(nodeId);
    lsSave(LS_SELECTED_NODE, nodeId);
  }

  /**
   * Navigates to the clicked tree node.
   * `extraQueryParams` are carried over as-is (e.g. the Holes tab `view`), so a sub-view survives
   * a blast change when the caller wants it to.
   */
  navigateFromNodeClick(
    event: { id: number | string; type: string },
    tab?: string,
    extraQueryParams: Record<string, string | null | undefined> = {},
  ): void {
    const siteId = this._siteId();
    if (!siteId) return;

    const type = event.type as SiteNodeType;

    switch (type) {
      case 'project': {
        const rawId     = String(event.id);
        const nodeValue = rawId.includes(':')
          ? rawId.split(':').slice(1).join(':')
          : rawId;
        const projetId  = this._data()?.find(b => b.nomProjet === nodeValue)?.projetId ?? null;
        if (projetId) {
          this._projectId.set(projetId);
          lsSave(LS_PROJECT_ID, String(projetId));
        }
        this.router.navigate(['/site', siteId], {
          queryParams: { ...extraQueryParams, projectId: projetId, nodeType: 'project', nodeValue, tab },
        });
        break;
      }

      case 'blast': {
        const blast    = this._data()?.find(b => b.tirId === Number(event.id));
        const projetId = blast?.projetId ?? null;
        if (projetId !== null) {
          this._projectId.set(projetId);
          lsSave(LS_PROJECT_ID, String(projetId));
        }
        this.router.navigate(['/site', siteId], {
          queryParams: {
            ...extraQueryParams,
            projectId: projetId,
            blastId:   event.id,
            nodeType:  'blast',
            nodeValue: blast?.nom ?? String(event.id),
            tab,
          },
        });
        break;
      }

      case 'zone':
      case 'year':
      case 'month':
      case 'niveau':
      case 'type': {
        const rawId = String(event.id);
        let nodeValue: string;
        let projetId: number | null;

        // Nouveau format : "Zone Nord~123" (projetId encodé dans l'ID)
        const tildeIdx = rawId.lastIndexOf('~');
        if (tildeIdx !== -1 && !isNaN(Number(rawId.slice(tildeIdx + 1)))) {
          nodeValue = rawId.slice(0, tildeIdx);
          projetId  = Number(rawId.slice(tildeIdx + 1));
        } else {
          // Ancien format : "zone:Zone Nord" ou valeur brute
          nodeValue = rawId.includes(':') ? rawId.split(':').slice(1).join(':') : rawId;
          projetId  = this._projectId();
        }

        if (projetId !== null) {
          this._projectId.set(projetId);
          lsSave(LS_PROJECT_ID, String(projetId));
        }
        this.router.navigate(['/site', siteId], {
          queryParams: { ...extraQueryParams, projectId: projetId, nodeType: type, nodeValue, tab },
        });
        break;
      }
    }
  }

  /** Libellé d'un nœud de groupement ouvert par l'URL : un mois s'affiche dans la langue d'Explore. */
  nodeLabel(nodeType: string | null, nodeValue: string | null): string {
    if (!nodeValue) return '';
    return nodeType === 'month' ? monthGroupLabel(nodeValue, this._lang()) : nodeValue;
  }

  /** Recharge la liste des blasts du site (après création, modification ou suppression). */
  reloadBlasts(): void {
    this._blastsVersion.update(version => version + 1);
  }

  /** Recharge le site courant s'il s'agit de `siteId` (après modification de sa configuration). */
  reloadSite(siteId: number): void {
    if (this._siteId() === siteId) this._siteVersion.update(version => version + 1);
  }

  /** Surcharge, pour le site courant et ce navigateur, la hiérarchie configurée sur le site. */
  setGrouping(config: TreeGroupingConfig): void {
    const siteId = this._siteId();
    if (siteId === null) return;
    const safe: TreeGroupingConfig = {
      ...config,
      level3: config.level2 === 'none' ? 'none' : config.level3,
    };
    this._localGrouping.set(safe);
    lsSave(`${LS_GROUPING}.${siteId}`, JSON.stringify(safe));
  }

  /** Abandonne la surcharge locale : l'arbre revient à la hiérarchie configurée sur le site. */
  resetGrouping(): void {
    const siteId = this._siteId();
    if (siteId === null) return;
    this._localGrouping.set(null);
    lsClear(`${LS_GROUPING}.${siteId}`);
  }

  setFilters(config: SiteTreeFilters): void {
    const safe: SiteTreeFilters = {
      ...defaultFilters(),
      ...config,
      endDate: config.endDate,
      startDate: config.startDate,
    };
    this._filters.set(safe);
    lsSave(LS_FILTERS, JSON.stringify(safe));
  }

  resetFilters(): void {
    // Sélections vides = « tout » : le popup les réaffiche cochées, et rien n'est retenu
    // du site courant qui viendrait masquer les tirs du site suivant.
    const safe = defaultFilters();
    this._filters.set(safe);
    lsSave(LS_FILTERS, JSON.stringify(safe));
  }

  /** Coche ou décoche des blasts de l'arbre ; l'état est mémorisé par site et pilote les tirs affichés sur la carte. */
  setBlastsChecked(blastIds: number[], checked: boolean): void {
    const siteId = this._siteId();
    if (siteId === null || !blastIds.length) return;
    const next = new Set(this._uncheckedBlastIds());
    blastIds.forEach(id => (checked ? next.delete(id) : next.add(id)));
    this._saveUncheckedBlasts(siteId, next);
  }

  setAllBlastsChecked(checked: boolean): void {
    const siteId = this._siteId();
    if (siteId === null) return;
    this._saveUncheckedBlasts(siteId, checked ? new Set() : new Set((this._data() ?? []).map(blast => blast.tirId)));
  }

  reset(): void {
    this._siteId.set(null);
    this._projectId.set(null);
    this._selectedNodeId.set('');
    this._filters.set(defaultFilters());
    lsClear(LS_SITE_ID);
    lsClear(LS_PROJECT_ID);
    lsClear(LS_SELECTED_NODE);
    lsClear(LS_FILTERS);
  }

  private _saveUncheckedBlasts(siteId: number, uncheckedBlastIds: Set<number>): void {
    this._uncheckedBlastIds.set(uncheckedBlastIds);
    lsSave(`${LS_UNCHECKED_BLASTS}.${siteId}`, JSON.stringify([...uncheckedBlastIds]));
  }

  // ── Builder dynamique ─────────────────────────────────────────────────────

  private _buildTree(blasts: BlastFlatDto[], config: TreeGroupingConfig, lang: string | undefined): TreeNode[] {
    const levels: GroupingLevel[] = [config.level1];
    if (config.level2 !== 'none') levels.push(config.level2);
    if (config.level3 !== 'none') levels.push(config.level3);
    return this._groupRecursive(blasts, levels, 0, lang);
  }

  private _groupRecursive(
    blasts: BlastFlatDto[],
    levels: GroupingLevel[],
    depth:  number,
    lang:   string | undefined,
    contextProjetId: number | null = null,
  ): TreeNode[] {
    if (depth >= levels.length) return this._mapBlasts(blasts, levels[levels.length - 1]);

    const level  = levels[depth];
    const groups = new Map<string, BlastFlatDto[]>();

    for (const blast of blasts) {
      if (level === 'month' && !getBestDate(blast)) continue;
      const key = getGroupValue(blast, level);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(blast);
    }

    const sortedKeys = sortGroupKeys(groups, level);

    return sortedKeys.map(key => {
      const groupBlasts = groups.get(key)!;

      // Détermine le projetId de contexte pour les enfants de ce nœud
      let childProjetId = contextProjetId;
      if (level === 'project') {
        const ids = [...new Set(
          groupBlasts.map(b => b.projetId).filter((id): id is number => id !== null)
        )];
        childProjetId = ids.length === 1 ? ids[0] : null;
      }

      // Pour les nœuds non-projet avec un contexte projet connu :
      // encoder le projetId dans l'ID ("Zone Nord~123") pour permettre
      // une navigation correcte entre nœuds de projets différents.
      // Pour les nœuds projet ou sans contexte : conserver le format "level:key".
      const nodeId = (level !== 'project' && childProjetId !== null)
        ? `${key}~${childProjetId}`
        : `${level}:${key}`;

      const children = this._groupRecursive(groupBlasts, levels, depth + 1, lang, childProjetId);
      return {
        id:       nodeId,
        type:     level,
        label:    level === 'month' ? monthGroupLabel(key, lang) : key,
        checked:  children.some(child => child.checked !== false),
        children,
      };
    });
  }

  /**
   * L'état coché est lu hors suivi : cocher une case ne doit pas reconstruire l'arbre
   * (la tree-view replierait ses nœuds), seul le signal visibleBlastIds est notifié.
   */
  private _mapBlasts(blasts: BlastFlatDto[], lastLevel: GroupingLevel): TreeNode[] {
    const unchecked = untracked(() => this._uncheckedBlastIds());
    return sortLeafBlasts(blasts, lastLevel)
      .map(b => ({
        id:      b.tirId,
        type:    'blast',
        label:   b.nom,
        checked: !unchecked.has(b.tirId),
      }));
  }
}
